import cx from 'clsx';
import React, { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router';

import { IconRcBack } from 'assets/overview';
import { login } from 'utils';

import { IconRcMobile } from '@/assets/icon/frame';
import { useCurrentAddressInfo, useSwitch, useDeviceType, DEVICE_TYPE } from '@/hooks';
import style from './index.module.css';

import SearchAddress from '../SearchAddress';
import { APPLoginModal, APPScanQRCodeModal } from './components';
import { APPLoginSuccessModal } from './components/AppLoginSuccessModal';
import { I18nSwitch } from './I18nSwitch';
import { GenerateQRCode } from './hooks';
import { NavLink } from '../NavLink';
import { IconMiniLogo, IconRcMainLogo } from '@/assets/sidebar';

type Props = {
  title: string;
  tagName?: string;
  className?: string;
  hasBottonBorder?: boolean;
  back?: boolean;
  additionalComp?: React.ReactNode;
  fixedTop?: boolean;
  onBackClick?(hasHistory: boolean): void;
  showLogo?: boolean;
};

export const DesktopHeader = (props: Props) => {
  const { title, tagName, back, onBackClick, fixedTop, additionalComp, showLogo } = props;
  const { isVerified } = useCurrentAddressInfo();
  const history = useHistory();
  const location = useLocation();
  const deviceType = useDeviceType();

  const mini = deviceType === DEVICE_TYPE.TABLET || deviceType === DEVICE_TYPE.MOBILE;

  const { on, turnOff, turnOn } = useSwitch();
  const {
    on: successLogin,
    turnOff: closeSucceededLoginModal,
    turnOn: openSuccessLoginModal,
  } = useSwitch();

  const [showLogin, setShowLogin] = useState(false);
  const [qrcodeData, setQRCodeData] = useState<GenerateQRCode>();

  const handleBack = useCallback(() => {
    if (onBackClick) {
      onBackClick(!!location.key);
    } else {
      // 在 app 内
      if (location.key) {
        history.goBack();
      } else {
        window.location.href = '/';
      }
    }
  }, [onBackClick, history, location]);

  const switchLogin = useCallback(
    (data: GenerateQRCode) => {
      turnOff();
      setQRCodeData(data);
      setShowLogin(true);
    },
    [turnOff],
  );

  const closeLogin = useCallback(() => {
    setQRCodeData(undefined);
    setShowLogin(false);
  }, []);

  const openQRcode = useCallback(() => {
    if (isVerified) {
      turnOn();
    } else {
      login();
    }
  }, [isVerified, turnOn]);

  return (
    <div
      className={cx(
        style.container,
        props.className,
        props.hasBottonBorder && style.border,
        fixedTop && style.fixedTop,
      )}>
      <div className={style.insideContainer}>
        {showLogo && (
          <div className={style.logo}>
            <NavLink to={'/'}>
              {mini ? <img src={IconMiniLogo} alt={''} /> : <IconRcMainLogo />}
            </NavLink>
          </div>
        )}
        <div className={style.navBack}>
          {back && (
            <div className={style.backBtn} onClick={handleBack}>
              <IconRcBack />
            </div>
          )}
          <h1 className={style.address}>
            {title}
            {tagName ? <span className={style.tag}>{tagName}</span> : null}
          </h1>
        </div>
        {additionalComp}
        <div className={style.options}>
          <SearchAddress
            inputClassName={style.headerSearch}
            isShowRecent={true}
            includeOfficialAccount
          />
          <I18nSwitch />
          <IconRcMobile className={style.mobile} onClick={openQRcode} />
        </div>
      </div>
      {on && <APPScanQRCodeModal turnOff={turnOff} switchLogin={switchLogin} />}
      {showLogin && (
        <APPLoginModal
          onSuccess={openSuccessLoginModal}
          close={closeLogin}
          qrcodeData={qrcodeData}
        />
      )}
      {successLogin && <APPLoginSuccessModal close={closeSucceededLoginModal} />}
    </div>
  );
};

export default React.memo(DesktopHeader);
export * from './PrivateEye';
